import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { MoneyTextInput } from '@/components/ui/money-input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info } from 'lucide-react';
import { formatCostInput, parseCostInput } from '@/lib/cost';
import {
  MARKUP_HELP,
  computePricing,
  parsePercentInput,
  type PricingMode,
} from '@/lib/pricing';

interface MarkupCalculatorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Custo atual do formulário, como TEXTO pt-BR (pode estar vazio). */
  cost: string;
  /**
   * Devolve o resultado ao formulário. O custo volta junto porque o caso mais
   * comum é o item AINDA NÃO TER custo: sem poder digitá-lo aqui, a calculadora
   * seria um beco sem saída ("informe o custo primeiro" e feche).
   */
  onApply: (result: { price: string; cost: string }) => void;
  /** Nome do item, só para o cabeçalho. */
  itemName?: string;
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatPercent = (value: number | null) =>
  value == null ? '—' : `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}%`;

/**
 * Calculadora de markup / margem de lucro.
 *
 * O valor principal dela não é a conta (que é uma linha), é DIDÁTICO: markup e
 * margem são confundidos o tempo todo, e quem aplica "50% de margem" com a
 * fórmula do markup ganha 33% e acha que ganhou 50%. Por isso a tela mostra
 * SEMPRE os dois indicadores do mesmo resultado, lado a lado.
 *
 * A conta em si mora em `src/lib/pricing.ts` (puro e testado) — aqui só há UI.
 */
export function MarkupCalculatorSheet({
  open,
  onOpenChange,
  cost,
  onApply,
  itemName,
}: MarkupCalculatorSheetProps) {
  const [mode, setMode] = useState<PricingMode>('markup');
  const [costText, setCostText] = useState(cost);
  const [percentText, setPercentText] = useState('');

  // Reabrir a calculadora tem que refletir o custo que está no formulário AGORA
  // (o usuário pode tê-lo preenchido no meio do caminho).
  useEffect(() => {
    if (open) setCostText(cost);
  }, [open, cost]);

  const parsedCost = useMemo(() => parseCostInput(costText), [costText]);
  const percent = useMemo(() => parsePercentInput(percentText), [percentText]);

  const result = useMemo(
    () => computePricing({ cost: parsedCost.ok ? parsedCost.value : null, percent, mode }),
    [parsedCost, percent, mode],
  );

  // Erro só depois de o usuário digitar algo: um sheet que abre já reclamando é
  // ruído, não ajuda.
  const showError = Boolean(result.error) && (costText.trim() !== '' || percentText.trim() !== '');

  const handleApply = () => {
    if (!result.ok || result.price == null) return;
    onApply({
      price: String(result.price).replace('.', ','),
      cost: formatCostInput(parsedCost.value),
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle>Calcular preço</SheetTitle>
          <SheetDescription>
            {itemName ? `A partir do custo de "${itemName}".` : 'A partir do custo do item.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Alternador markup × margem — Tabs herda a pill azul do componente
              base (não é um CTA, então não usa o verde do app). */}
          <Tabs value={mode} onValueChange={(v) => setMode(v as PricingMode)}>
            <TabsList className="w-full">
              <TabsTrigger value="markup" className="flex-1">
                Markup
              </TabsTrigger>
              <TabsTrigger value="margin" className="flex-1">
                Margem de lucro
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{MARKUP_HELP}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="calc-cost">Custo</Label>
              {/* Mesma máscara do campo de custo do formulário: a calculadora
                  LÊ e DEVOLVE aquele valor, e dois formatos diferentes fariam o
                  round-trip corromper o número (#153a). */}
              <MoneyTextInput
                id="calc-cost"
                value={costText}
                onChange={setCostText}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="calc-percent">{mode === 'markup' ? 'Markup (%)' : 'Margem (%)'}</Label>
              <Input
                id="calc-percent"
                inputMode="decimal"
                placeholder={mode === 'markup' ? 'Ex: 50' : 'Ex: 33,3'}
                value={percentText}
                onChange={(e) => setPercentText(e.target.value)}
              />
            </div>
          </div>

          {showError && (
            <p role="alert" className="text-sm text-amber-600">
              {result.error}
            </p>
          )}

          {result.ok && result.price != null && (
            <div className="space-y-3 rounded-lg border p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Venda</span>
                <span className="text-2xl font-semibold">{formatBRL(result.price)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Lucro</span>
                <span className="text-base font-medium">{formatBRL(result.profit ?? 0)}</span>
              </div>
              {/* Os DOIS indicadores, sempre — é o ponto da tela. O que o
                  usuário não digitou aparece em cinza, para ele ver que "50% de
                  markup" não é "50% de margem". */}
              <div className="grid grid-cols-2 gap-2 border-t pt-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Markup</p>
                  <p className={mode === 'markup' ? 'font-semibold' : 'text-muted-foreground'}>
                    {formatPercent(result.markupPercent)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Margem</p>
                  <p className={mode === 'margin' ? 'font-semibold' : 'text-muted-foreground'}>
                    {formatPercent(result.marginPercent)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button3D color="gray" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button3D>
            <Button3D className="flex-1" onClick={handleApply} disabled={!result.ok}>
              Usar este preço
            </Button3D>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
